import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import { Box, Stack, TableCell, TableRow, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { useGetTrackedEventQuery } from '../../../../api/core'
import { formatDate } from '../../../../helpers/date'
import { UserLanguage } from '../../../account/types/User'
import { LiveEventEvolutionEntry } from '../../../live-events/types/LiveEventCollection'
import { TrackedGame } from '../../../live-events/types/TrackedGame'
import { BlueprintEventKpi } from '../../hooks/useBlueprintEventKpis'
import { BlueprintEventRowActions, EVENT_LINK_CLASS, EventTypeTagGroup } from '../BlueprintEventRowActions/BlueprintEventRowActions'
import { EventStatusDot } from '../EventStatusDot/EventStatusDot'
import { BlueprintEventRow, GAP_WARNING_THRESHOLD_DAYS } from '../helpers/blueprintEventRows'
import { buildLiveEventsEventModalUrl } from '../helpers/calendarLinks'
import { DRILLDOWN_CELL_CLASS, DURATION_BAR_MAX_WIDTH, EVENT_ROW_HEIGHT, eventDividerColor, expandedBlockRowSx } from '../helpers/drilldownTableLayout'
import { instanceFlagsFromEvolution, trackingEventFromEventGetter } from '../helpers/eventTooltipData'

export const formatEventDate = (timestamp: number, language: UserLanguage) =>
  formatDate(new Date(timestamp), { language, year: 'numeric', month: 'short', day: 'numeric' })

// Hook for the row hover rule: the event name is the deep link into the tracker.
const EVENT_NAME_LINK_CLASS = 'DrilldownEventRow__name'

const numericSx = { fontVariantNumeric: 'tabular-nums' } as const
const nameSx = { fontSize: 'inherit', fontWeight: 700 } as const

export interface DrilldownEventRowProps {
  // DOM id the blueprint group row's toggle points at.
  id?: string
  row: BlueprintEventRow
  // 1-based position within its blueprint group, in the current sort.
  index: number
  // Longest event of the game — the duration bar scales to it.
  maxDurationDays: number
  userLanguage: UserLanguage
  gameId: string
  eventTypeId: string
  // Type to link with when the family has none and the event's own type has not arrived yet.
  fallbackEventTypeId?: string
  trackedGame?: TrackedGame
  // The blueprint-evolution entries of this event: where the popup's NEW and first-appearance flags
  // come from, which the event getter does not carry. `undefined` while the evolution is loading, so the
  // popup waits rather than show flags from another source; `[]` when it loaded (or failed) with none.
  evolutionEntries?: LiveEventEvolutionEntry[]
  tagGroupsByEventTypeId: { [eventTypeId: string]: EventTypeTagGroup }
  // Popup colour of an event whose type has no tag group.
  popupBgColor: string
  // The KPI chips of the popup; absent when the instance has none (not ended, no data, not a mobile game).
  kpi?: BlueprintEventKpi
}

/**
 * One event inside an expanded game block of the adopters tree table. Shares
 * the game rows' column grid: number under the rank, name aligned with the
 * game name, dates, duration bar, gap-before (breaks > 7 d in warning red),
 * status dot, and the always-visible icon rail (Mechanic Change and reskin
 * markers, then the details icon) under the "Reskin · links" column.
 */
const DrilldownEventRowComponent: React.FC<DrilldownEventRowProps> = ({
  id,
  row,
  index,
  maxDurationDays,
  userLanguage,
  gameId,
  eventTypeId,
  fallbackEventTypeId,
  trackedGame,
  evolutionEntries,
  tagGroupsByEventTypeId,
  popupBgColor,
  kpi,
}) => {
  const { t } = useTranslation()
  // The row's info popup reads the event getter. Rows mount when their blueprint opens, so the request
  // is in flight before the first hover; instances of one event share a single cached response.
  const { data: eventResponse } = useGetTrackedEventQuery(row.event.eventId, { skip: !row.event.eventId })
  const trackingEvent = useMemo(() => {
    if (!eventResponse) return undefined
    const flags = instanceFlagsFromEvolution(evolutionEntries, row.startDate)
    return trackingEventFromEventGetter(eventResponse, {
      start: row.startDate,
      end: row.endDate,
      highlighted: flags?.highlighted ?? false,
      firstInstance: flags?.firstInstance,
    })
  }, [eventResponse, evolutionEntries, row.startDate, row.endDate])
  // The popup shows NEW and first-appearance lines, so it holds back until the evolution flags are in.
  const popupEvent = evolutionEntries ? trackingEvent : undefined
  const tagGroup = trackingEvent?.typeId ? tagGroupsByEventTypeId[trackingEvent.typeId] : undefined
  const barWidth = Math.max(6, Math.round((row.durationDays / Math.max(1, maxDurationDays)) * DURATION_BAR_MAX_WIDTH))
  const isBreak = row.gapBeforeDays !== null && row.gapBeforeDays > GAP_WARNING_THRESHOLD_DAYS
  const name = row.name || formatEventDate(row.startDate, userLanguage)
  const resolvedEventTypeId = eventTypeId || trackingEvent?.typeId || fallbackEventTypeId
  const trackerPath =
    row.event.eventId && resolvedEventTypeId
      ? buildLiveEventsEventModalUrl({
          gameId,
          eventTypeId: resolvedEventTypeId,
          eventId: row.event.eventId,
          durationStart: row.startDate,
          durationEnd: row.endDate,
        })
      : undefined

  return (
    <TableRow
      id={id}
      data-testid="drilldown-event-row"
      sx={(theme) => ({
        ...expandedBlockRowSx(theme),
        height: EVENT_ROW_HEIGHT,
        '& > td': { borderBottomColor: eventDividerColor(theme), fontSize: theme.typography.h4.fontSize },
        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.07) },
        // Whole-row hover wakes the links: the grey rail darkens, the event name turns brand purple.
        [`&:hover .${EVENT_LINK_CLASS}, &:focus-within .${EVENT_LINK_CLASS}`]: { color: alpha(theme.palette.text.primary, 0.7) },
        [`&:hover .${EVENT_NAME_LINK_CLASS}, &:focus-within .${EVENT_NAME_LINK_CLASS}`]: { color: theme.palette.primary.main },
      })}
    >
      <TableCell className={DRILLDOWN_CELL_CLASS.eventNumber}>
        <Typography variant="caption" component="span" sx={{ ...numericSx, color: 'text.disabled' }}>
          {index}
        </Typography>
      </TableCell>
      <TableCell className={DRILLDOWN_CELL_CLASS.eventName}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          {trackerPath ? (
            <Typography
              variant="body2"
              noWrap
              component={RouterLink}
              to={trackerPath}
              target="_blank"
              rel="noopener noreferrer"
              className={EVENT_NAME_LINK_CLASS}
              title={row.event.name || name}
              sx={{ ...nameSx, display: 'block', minWidth: 0, color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {name}
            </Typography>
          ) : (
            // Events without an id (older payloads) have nothing to open.
            <Typography variant="body2" noWrap title={row.event.name || name} sx={{ ...nameSx, minWidth: 0 }}>
              {name}
            </Typography>
          )}
        </Stack>
      </TableCell>
      <TableCell sx={numericSx}>{formatEventDate(row.startDate, userLanguage)}</TableCell>
      <TableCell sx={numericSx}>{formatEventDate(row.endDate, userLanguage)}</TableCell>
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box aria-hidden sx={(theme) => ({ width: barWidth, height: 4, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.35), flexShrink: 0 })} />
          <Box component="span" sx={numericSx}>
            {t('market-analytics:event_table_duration_days', { count: row.durationDays })}
          </Box>
        </Stack>
      </TableCell>
      <TableCell>
        {row.gapBeforeDays === null ? (
          <Box component="span" sx={{ color: 'text.disabled' }}>
            {'\u2014'}
          </Box>
        ) : (
          <Box component="span" sx={{ ...numericSx, color: isBreak ? 'warning.main' : 'text.secondary', fontWeight: isBreak ? 700 : 'normal' }}>
            {t('market-analytics:event_table_days_short', { count: row.gapBeforeDays })}
          </Box>
        )}
      </TableCell>
      <TableCell>
        <EventStatusDot status={row.status} />
      </TableCell>
      <TableCell align="right" className={DRILLDOWN_CELL_CLASS.actions}>
        <BlueprintEventRowActions
          row={row}
          trackingEvent={popupEvent}
          tagGroup={tagGroup}
          popupBgColor={tagGroup?.colorHex || popupBgColor}
          trackedGame={trackedGame}
          userLanguage={userLanguage}
          kpi={kpi}
        />
      </TableCell>
    </TableRow>
  )
}

export const DrilldownEventRow = React.memo(DrilldownEventRowComponent)
DrilldownEventRow.displayName = 'DrilldownEventRow'
